import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faCalendar, faClock, faMessage, faUser, faStethoscope, faCheckCircle, faInfo } from "@fortawesome/free-solid-svg-icons";
import "../components/componentsCss/SchedulingPage.css";

const SchedulingPage = ({ patient, procedures, doctor, onSubmit, onBack }) => {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [time, setTime] = useState("");
  const [comments, setComments] = useState("Sem observações");
  const [errors, setErrors] = useState({});

  const getSellingPrice = (proc) => {
    const byId =
      doctor?.procedures?.find((p) => p.id === proc.procedure_id)
        ?.selling_price ??
      doctor?.procedures?.find((p) => p.id === proc.id)?.selling_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.value ?? proc.cost_price ?? proc.price ?? 0);
  };

  const getProcCostPrice = (proc) => {
    const byId =
      doctor?.procedures?.find((p) => p.id === proc.procedure_id)?.cost_price ??
      doctor?.procedures?.find((p) => p.id === proc.id)?.cost_price;

    if (typeof byId === "number" && !isNaN(byId)) return byId;

    return Number(proc.cost_price ?? 0);
  };

  /** true se **todos** forem convênio */
  const allInsurance = procedures.every((p) => p.payment?.type === "insurance");

  /** Soma apenas os particulares */
  const totalPrice = useMemo(
    () =>
      procedures.reduce((sum, proc) => {
        if (proc.payment?.type === "insurance") return sum;
        return sum + getSellingPrice(proc);
      }, 0),
    [procedures]
  );

  const formatCurrency = (v = 0) =>
    `R$ ${Number(v).toFixed(2).replace(".", ",")}`;

  /* ─────────── horários disponíveis (exemplo) ─────────── */

  const today = new Date().toISOString().split("T")[0];
  // const maxDate = new Date();
  // maxDate.setMonth(maxDate.getMonth() + 3);
  // const maxDateStr = maxDate.toISOString().split("T")[0];

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!date) newErrors.date = "Selecione uma data";
    if (!time) newErrors.time = "Selecione um horário";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    // console.log("✅ Enviando agendamento:", {
    //   date,
    //   time,
    //   comments,
    //   doctor,
    //   procedures,
    //   patient,
    // });

    onSubmit({ date, time, comments, doctor, procedures, patient });
  };

  /* ─────────── JSX ─────────── */
  return (
    <div className="page-container scheduling-page">
      <div className="page-header">
        <button className="back-button" onClick={onBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Voltar</span>
        </button>
        <h1>Agendar Procedimentos</h1>
      </div>

      <div className="scheduling-content">
        {/* ---------- Info cards ---------- */}
        <div className="scheduling-info">
          {/* Paciente */}
          <div className="info-card patient-info-card">
            <div className="info-card-header">
              <FontAwesomeIcon icon={faUser} />
              <h3>Paciente</h3>
            </div>
            <div className="info-card-content">
              <h4>{patient.name}</h4>
              <p>CPF: {patient.cpf}</p>
            </div>
          </div>

          {/* Profissional */}
          <div className="info-card doctor-info-card">
            <div className="info-card-header">
              <FontAwesomeIcon icon={faStethoscope} />
              <h3>Profissional</h3>
            </div>
            <div className="info-card-content">
              <h4>{doctor.name}</h4>
              <p>{doctor.specialty}</p>
            </div>
          </div>

          {/* Procedimentos */}
          <div className="info-card procedures-info-card">
            <div className="info-card-header">
              <FontAwesomeIcon icon={faCheckCircle} />
              <h3>Procedimentos</h3>
            </div>
            <div className="info-card-content">
              <ul className="procedures-list-summary">
                {procedures.map((proc) => {
                  const sellingPrice = getSellingPrice(proc);
                  const costPrice = getProcCostPrice(proc);
                  return (
                    <li key={proc.id}>
                      <span>{proc.name}</span>
                      <span className="procedure-price">
                        {proc.payment?.type === "insurance"
                          ? "Convênio"
                          : formatCurrency(sellingPrice)}
                      </span>
                    </li>
                  );
                })}
              </ul>

              <div className="total-price-container">
                <span>Valor&nbsp;Total:</span>
                <span className="total-price-value">
                  {allInsurance ? "Convênio" : formatCurrency(totalPrice)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Formulário ---------- */}
        <form className="scheduling-form" onSubmit={handleSubmit}>
          <h3 className="form-title">Selecione data e horário</h3>

          <div className="form-row">
            {/* Data */}
            <div className="form-group">
              <label htmlFor="date">
                <FontAwesomeIcon icon={faCalendar} />
                <span>Data</span>
              </label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={today}
                //max={maxDateStr}
                className={errors.date ? "error" : ""}
              />
              {errors.date && <p className="error-message">{errors.date}</p>}
            </div>

            {/* Horário */}
            <div className="form-group">
              <label htmlFor="time">
                <FontAwesomeIcon icon={faClock} />
                <span>Horário</span>
              </label>
              <input
                type="time"
                id="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className={errors.time ? "error" : ""}
              />
              {errors.time && <p className="error-message">{errors.time}</p>}
            </div>
          </div>

          {/* Observações */}
          <div className="form-group comments-group">
            <label htmlFor="comments">
              <FontAwesomeIcon icon={faMessage} />
              <span>Observações</span>
            </label>
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value || "Sem observações")}
              placeholder="Adicione informações relevantes para o agendamento (opcional)"
              rows={4}
            />
          </div>

          {/* Nota */}
          <div className="scheduling-note">
            <FontAwesomeIcon icon={faInfo} />
            <p>
              Ao confirmar o agendamento, você concorda com os termos e
              políticas da clínica. Cancelamentos devem ser realizados com pelo
              menos 24 horas de antecedência.
            </p>
          </div>

          {/* Ações */}
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onBack}>
              Cancelar
            </button>
            <button type="submit" className="submit-button">
              Revisar e Confirmar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SchedulingPage;
